/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { useMemo } from 'react';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Box,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
  Fade,
  useTheme,
  alpha,
} from '@mui/material';
import ChartWatermarkContainer from 'components/shared/ChartWatermarkContainer';
import { ProfitLossData, ProfitLossWithDomain } from 'types/riskEngine';
import { useOptionsCalculatorComputed } from 'hooks/optionsCalculator/useOptionsCalculatorComputed';
import { useOptionsCalculatorData } from 'hooks/optionsCalculator/useOptionsCalculatorData';
import { useRecoilValue } from 'recoil';
import {
  D3PnlPlotDataPoint,
  D3PnlPlotLine,
  OptionType,
  PositionLeg,
  PositionShares,
  StrikeDeltaObject,
} from 'types/optionsCalculator';
import { GREEK_IDX, OPTION_IDX, RawGreeksDataMap } from 'types/impliedVol';
import { findNearestIdx, Order, roundToNearest } from 'util/shared';
import { negativeTrendColorState, positiveTrendColorState } from 'states';
import { hasModifiedIVSettings } from 'util/optionsCalculator';
import D3PnlChart from './pnlChart/D3PnlChart';

const MIN_ZOOM_LEVEL = 0.5;
const MAX_ZOOM_LEVEL = 100;
const SLOPE_EPSILON = 3.0; // Minimum PnL absolute slope to for x-zoom purposes

const Chart = ({
  data,
  dataAtExpiration,
  dataUnadjustedIv,
  currentPrice,
  currentKeyLevels,
  showKeyLevels,
  showLegend,
  forceDefaultZoom,
  setForceDefaultZoom,
  legs,
  sgGreeks,
  sym,
  shares,
  timeToExpiration,
  disabledExpirationOptions,
  expirationOptions,
  onLegChange,
  strikeDataPerExpiration,
  onLegDelete,
}: {
  data: ProfitLossWithDomain;
  dataAtExpiration?: ProfitLossWithDomain;
  dataUnadjustedIv?: ProfitLossWithDomain;
  currentPrice?: number;
  currentKeyLevels?: { field: string; value: number; name: string }[];
  showKeyLevels: boolean;
  showLegend: boolean;
  forceDefaultZoom: boolean;
  setForceDefaultZoom: (forceDefaultZoom: boolean) => void;
  legs: PositionLeg[];
  sgGreeks?: RawGreeksDataMap;
  sym?: string;
  shares: PositionShares[];
  timeToExpiration: number;
  onLegChange: (
    legId: number,
    updates: Partial<PositionLeg>,
    shouldUseDefaultPrice: boolean,
  ) => void;
  strikeDataPerExpiration: Map<number, StrikeDeltaObject[]>;
  disabledExpirationOptions: number[];
  expirationOptions: number[];
  onLegDelete?: (legId: number) => void;
}) => {
  const theme = useTheme();
  const { pnlChartRef } = useOptionsCalculatorComputed();
  const positiveColor = useRecoilValue(positiveTrendColorState);
  const negativeColor = useRecoilValue(negativeTrendColorState);
  const fieldColorMapping = theme.palette.equityHub.fieldColorMapping;

  const getValueColor = (value: number) => {
    return value > 0 ? positiveColor : negativeColor;
  };

  const getPnlData = (d: ProfitLossData): D3PnlPlotDataPoint => ({
    x: d.price,
    y: d.profit,
    attributes: [
      ...(d.delta != null && d.delta !== 0
        ? [
            {
              label: 'Delta',
              value: d.delta,
              color: getValueColor(d.delta),
            },
          ]
        : []),
      ...(d.gamma != null && d.gamma !== 0
        ? [
            {
              label: 'Gamma',
              value: d.gamma,
              color: getValueColor(d.gamma),
            },
          ]
        : []),
      ...(d.vega != null && d.vega !== 0
        ? [
            {
              label: 'Vega',
              value: d.vega,
              color: getValueColor(d.vega),
            },
          ]
        : []),
      ...(d.theta != null && d.theta !== 0
        ? [
            {
              label: 'Theta',
              value: d.theta,
              color: getValueColor(d.theta),
            },
          ]
        : []),
    ],
  });

  const lines: D3PnlPlotLine[] = [
    {
      data: data.profitLossData.map(getPnlData),
      stroke: '#8888FF',
      fill: 'none',
      label: dataUnadjustedIv ? 'IV-Adjusted PnL' : 'Current PnL',
    },
    // Only show At Expiration PnL if data is provided
    ...(dataAtExpiration
      ? [
          {
            data: dataAtExpiration.profitLossData.map(getPnlData),
            stroke: '#888888',
            fill: 'none',
            strokeDasharray: '5,5',
            opacity: 0.5,
            label: 'At Expiration PnL',
          },
        ]
      : []),
    // Only show Unadjusted IV PnL if data is provided
    ...(dataUnadjustedIv
      ? [
          {
            data: dataUnadjustedIv.profitLossData.map(getPnlData),
            stroke: theme.palette.warning.main,
            fill: 'none',
            strokeDasharray: '5,5',
            label: 'Market IV PnL',
          },
        ]
      : []),
  ];

  const areas = [
    {
      data: data.profitLossData.map((d) => [d.price, 0, Math.max(d.profit, 0)]),
      fill: positiveColor,
      opacity: 0.15,
    },
    {
      data: data.profitLossData.map((d) => [d.price, 0, Math.min(d.profit, 0)]),
      fill: negativeColor,
      opacity: 0.15,
    },
  ];

  const yDomain = useMemo(() => {
    // Collect all y values from both datasets
    const currentValues = data.profitLossData.map((d) => d.profit);
    const expirationValues =
      dataAtExpiration?.profitLossData.map((d) => d.profit) ?? [];
    const unadjustedIvValues =
      dataUnadjustedIv?.profitLossData.map((d) => d.profit) ?? [];
    const boundsValues = data.profitLossData.flatMap((d) => d.profitBounds);

    // Combine all values to find the true min/max
    const allValues = [
      ...currentValues,
      ...expirationValues,
      ...boundsValues,
      ...unadjustedIvValues,
    ];

    const minY = Math.min(...allValues);
    const maxY = Math.max(...allValues);
    const range = maxY - minY;
    const padding = range * 0.05;

    const minDomain = roundToNearest(minY - padding, 0.001); // round to 3 decimal places
    const maxDomain = roundToNearest(maxY + padding, 0.001); // round to 3 decimal places

    return [minDomain, maxDomain];
  }, [data.profitLossData, dataAtExpiration, dataUnadjustedIv]);

  const defaultZoom = useMemo(() => {
    const legStrikes = legs.map((leg) => leg.strike);
    // Handle case where no legs are present
    if (sgGreeks == null || currentPrice == null || legs.length === 0) {
      // Use domain midpoint as fallback
      const midpoint = (data.domain[0] + data.domain[1]) / 2;
      return {
        x: midpoint,
        y: 0, // Center on zero profit/loss
        kX: MIN_ZOOM_LEVEL, // Reduced default zoom level to show more of y-axis
        kY: MIN_ZOOM_LEVEL, // Reduced default zoom level to show more of y-axis
      };
    }

    // Calculate the range of all strikes for bound calculation
    const minStrike = Math.min(currentPrice, ...legStrikes);
    const maxStrike = Math.max(currentPrice, ...legStrikes);

    // Get nearest term expiration from user's chosen positions
    const nearestExpiration = Math.min(...legs.map((leg) => leg.expiry));

    const {
      profitLossData: pnls,
      breakEvenPrices,
      maxProfit,
      maxLoss,
      netPutPosition,
    } = data;
    const greeksObject = sgGreeks[nearestExpiration];
    if (greeksObject == null) {
      return null;
    }
    const strikes = Object.keys(greeksObject).map(Number);
    const atmStrikeIdx = findNearestIdx(strikes, currentPrice, Order.ASC);
    const atmStrike = strikes[atmStrikeIdx];
    const atmCallGreeks = greeksObject[atmStrike][OPTION_IDX.CALL];
    const atmPutGreeks = greeksObject[atmStrike][OPTION_IDX.PUT];
    const atmCall =
      atmCallGreeks && atmPutGreeks
        ? (atmCallGreeks[GREEK_IDX.BID] + atmCallGreeks[GREEK_IDX.ASK]) / 2
        : undefined;
    const atmPut =
      atmCallGreeks && atmPutGreeks
        ? (atmPutGreeks[GREEK_IDX.BID] + atmPutGreeks[GREEK_IDX.ASK]) / 2
        : undefined;

    // Calculate straddle price (ATM call price + ATM put price)
    const straddlePrice =
      atmCall && atmPut ? atmCall + atmPut : (maxStrike - minStrike) * 0.1; // Fallback: use 10% of strike range if options data not available

    // Calculate the PnL range to ensure peaks and troughs are visible
    const pnlValues = pnls.map((d) => d.profit);
    const minPnL = Math.min(...pnlValues);
    const maxPnL = Math.max(...pnlValues);
    const fullPnlRange = maxPnL - minPnL;

    // Find the price range where significant PnL changes occur
    const significantPnlThreshold = fullPnlRange * 0.1; // 10% of total PnL range
    const sigPoints = pnls
      .filter((d, idx) => {
        // Filter out "uninteresting" flat curves.  i.e. we don't need the flat PnL wings of an iron condor
        const neighbor =
          idx == 0 || (d.price > currentPrice && idx < pnls.length - 1)
            ? idx + 1
            : idx - 1;
        const slope =
          (d.profit - pnls[neighbor].profit) / (d.price - pnls[neighbor].price);
        return Math.abs(slope) > SLOPE_EPSILON;
      })
      .filter(
        (d) =>
          Math.abs(d.profit) > significantPnlThreshold ||
          Math.abs(d.profit - minPnL) < significantPnlThreshold ||
          Math.abs(d.profit - maxPnL) < significantPnlThreshold,
      );

    // Calculate bounds based on both strikes and significant PnL points
    const strikeRange = maxStrike - minStrike;
    const strikePadding = Math.max(straddlePrice * 0.3, strikeRange * 0.3);

    const sigPrices = sigPoints.map((d) => d.price);
    const minSignificantPrice = sigPrices.length > 0 ? sigPrices[0] : minStrike;
    const maxSignificantPrice =
      sigPrices.length > 0 ? sigPrices[sigPrices.length - 1] : maxStrike;

    // Use the wider of the two ranges to ensure both strikes and PnL extremes are visible
    let lowerBound =
      Math.min(minStrike, ...(breakEvenPrices ?? [])) - strikePadding;
    const useMinSigPrice =
      sigPoints.length === 0 ||
      (sigPoints[0].profit > 0
        ? maxProfit !== Infinity && (netPutPosition ?? 1) <= 0
        : maxLoss !== -Infinity);
    if (useMinSigPrice) {
      lowerBound = Math.min(
        lowerBound,
        minSignificantPrice - straddlePrice * 0.3,
      );
    }

    let upperBound =
      Math.max(maxStrike, ...(breakEvenPrices ?? [])) + strikePadding;
    const useMaxSigPrice =
      sigPoints.length === 0 ||
      (sigPoints[sigPoints.length - 1].profit > 0
        ? maxProfit !== Infinity
        : maxLoss !== -Infinity);
    if (useMaxSigPrice) {
      upperBound = Math.max(
        upperBound,
        maxSignificantPrice + straddlePrice * 0.3,
      );
    }

    if (Array.isArray(breakEvenPrices) && breakEvenPrices.length > 0) {
      // Consider a long ITM call scenario. The lower bound will have minStrike
      // or minSignificantPrice, while the upper bound has just current price.
      // To make up for that and to better center the view on the 0-intercept,
      // ensure our upper bound is at least as far away from the max
      // 0-interecept as the lower bound is from the min 0-intercept
      if (!useMaxSigPrice) {
        const lowerDist = Math.abs(Math.min(...breakEvenPrices) - lowerBound);
        upperBound = Math.max(
          upperBound,
          Math.max(...breakEvenPrices) + lowerDist,
        );
      }

      // Same as above, but a long put scenario is what we're "fixing"
      if (!useMinSigPrice) {
        const upperDist = Math.abs(Math.max(...breakEvenPrices) - upperBound);
        lowerBound = Math.min(
          lowerBound,
          Math.min(...breakEvenPrices) - upperDist,
        );
      }
    }

    // The visual domain can't exceed our domain we *actually* created
    upperBound = Math.min(upperBound, data.domain[1]);
    lowerBound = Math.max(lowerBound, data.domain[0]);

    // sigPoints' fixed slope threshold can skew a gradually-decaying curve (e.g. a
    // diagonal) off-center - shift onto the one breakeven when there's exactly one.
    if (Array.isArray(breakEvenPrices) && breakEvenPrices.length === 1) {
      const [recenterAnchor] = breakEvenPrices;
      const shift = recenterAnchor - (lowerBound + upperBound) / 2;
      lowerBound = Math.max(data.domain[0], lowerBound + shift);
      upperBound = Math.min(data.domain[1], upperBound + shift);
    }

    const desiredRange = upperBound - lowerBound;
    const fullRange = data.domain[1] - data.domain[0];

    // Calculate zoom level to show the desired range
    const baseZoomLevel = fullRange / desiredRange;
    const xZoomLevel = Math.min(baseZoomLevel, MAX_ZOOM_LEVEL);

    // Smart Y zoom calculation to show peak to trough
    const fullYRange = yDomain[1] - yDomain[0];

    // Limit our desired y-axis range to what we'll actually display based on x-axis range
    const visiblePnLValues = pnls
      .filter((d) => d.price >= lowerBound && d.price <= upperBound)
      .map((d) => d.profit);
    const maxVisPnL = Math.max(...visiblePnLValues);
    let minVisPnL = Math.min(...visiblePnLValues);
    // Unbounded risk (naked strangles/straddles) has no loss cap, so normal
    // strike padding alone can swamp the y-range and hide the real profit.
    if (maxLoss === -Infinity && maxVisPnL > 0) {
      minVisPnL = Math.max(minVisPnL, -3 * maxVisPnL);
    }
    const pnlRange = maxVisPnL - minVisPnL;

    // Add padding to PnL range for better visibility
    const pnlPadding = pnlRange * 0.1; // 10% padding
    const desiredYRange = pnlRange + 2 * pnlPadding;

    // Calculate Y zoom level to show the full PnL range with padding
    const baseYZoomLevel = fullYRange / desiredYRange;
    const yZoomLevel = Math.min(baseYZoomLevel, MAX_ZOOM_LEVEL);

    // Center Y on the midpoint between min and max PnL
    const yCenter = (minVisPnL + maxVisPnL) / 2;
    const xCenter = (lowerBound + upperBound) / 2;
    return {
      x: xCenter, // Center on our desired bounds
      y: yCenter, // Center on PnL midpoint to show peak to trough
      kX: Math.max(xZoomLevel, MIN_ZOOM_LEVEL),
      kY: Math.max(yZoomLevel, MIN_ZOOM_LEVEL),
    };
  }, [data, currentPrice, legs, sgGreeks, yDomain]);

  const referenceLines: D3PnlPlotLine[] = useMemo(
    () =>
      currentKeyLevels?.map((level) => {
        const stroke =
          fieldColorMapping[
            level.field?.split(',')[0] as keyof typeof fieldColorMapping
          ] || '#888888';

        return {
          data: [
            {
              x: level.value,
              y: yDomain[0],
              attributes: [],
            },
            {
              x: level.value,
              y: yDomain[1],
              attributes: [],
            },
          ],
          stroke: stroke,
          strokeWidth: 2,
          opacity: 0.7,
          label: level.name,
        };
      }) ?? [],
    [currentKeyLevels, yDomain, fieldColorMapping],
  );

  const totalDelta = useMemo(() => {
    if (currentPrice == null || !sgGreeks) {
      return 0;
    }

    return legs.reduce((sum, leg) => {
      const greeksForExpiry = sgGreeks[leg.expiry];
      if (!greeksForExpiry || !greeksForExpiry[leg.strike]) {
        return sum;
      }

      const optionData = greeksForExpiry[leg.strike];
      const optionTypeIndex =
        leg.option_type === OptionType.CALL ? OPTION_IDX.CALL : OPTION_IDX.PUT;
      const delta = optionData[optionTypeIndex][GREEK_IDX.DELTA];

      return sum + leg.quantity * (delta ?? 0);
    }, 0);
  }, [legs, currentPrice, sgGreeks]);

  if (defaultZoom == null) {
    return null;
  }

  return (
    <D3PnlChart
      ref={pnlChartRef}
      xDomain={data.domain}
      yDomain={yDomain}
      disabledExpirationOptions={disabledExpirationOptions}
      expirationOptions={expirationOptions}
      xAxisLabel="Stock Price ($)"
      yAxisLabel="Profit/Loss ($)"
      lines={lines}
      areas={areas}
      optionLegs={legs}
      shares={shares}
      sym={sym}
      timeToExpiration={timeToExpiration}
      currentPrice={currentPrice}
      referenceLines={showKeyLevels ? referenceLines : []}
      showLegend={showLegend}
      defaultZoom={defaultZoom}
      forceDefaultZoom={forceDefaultZoom}
      setForceDefaultZoom={setForceDefaultZoom}
      summary={
        data.maxProfit != null && data.maxLoss != null
          ? {
              maxProfit: data.maxProfit,
              maxLoss: data.maxLoss,
              breakEvenPrices: data.breakEvenPrices || [],
              totalDelta,
            }
          : undefined
      }
      showStrikePicker={true}
      onLegChange={onLegChange}
      onLegDelete={onLegDelete}
      strikeDataPerExpiration={strikeDataPerExpiration}
    />
  );
};

const LoadingSkeleton = () => (
  <Box sx={{ width: '100%', height: '300px', p: 2 }}>
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Skeleton variant="text" width="30%" height={30} />
        <Skeleton variant="text" width="20%" height={30} />
      </Box>
      <Box sx={{ display: 'flex', height: '220px' }}>
        <Skeleton
          variant="rectangular"
          width="40px"
          height="100%"
          sx={{ mr: 1 }}
        />
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          {Array(5)
            .fill(0)
            .map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                width="100%"
                height="1px"
              />
            ))}
          <Skeleton variant="rectangular" width="100%" height="40px" />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    </Stack>
  </Box>
);

const LoadingOverlay = ({
  isCalculating,
  calculationError,
}: {
  isCalculating: boolean;
  calculationError?: string | null;
}) => {
  const theme = useTheme();
  return (
    <Fade in={isCalculating} timeout={300}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          backdropFilter: 'blur(8px)',
          borderRadius: 1,
        }}
      >
        <CircularProgress size={48} thickness={4} />
        <Typography
          variant="body2"
          sx={{ mt: 2, fontWeight: 500, color: 'primary.main' }}
        >
          Recalculating PnL...
        </Typography>
        {calculationError && (
          <Typography variant="caption" sx={{ mt: 1, color: 'error.main' }}>
            Error: {calculationError}
          </Typography>
        )}
      </Box>
    </Fade>
  );
};

interface PnLChartProps {
  showKeyLevels: boolean;
  showLegend: boolean;
  chartHeight?: number;
  forceDefaultZoom: boolean;
  setForceDefaultZoom: (forceDefaultZoom: boolean) => void;
  isFullscreen: boolean;
  onLegChange: (
    legId: number,
    updates: Partial<PositionLeg>,
    shouldUseDefaultPrice: boolean,
  ) => void;
  onLegDelete?: (legId: number) => void;
  strikeDataPerExpiration: Map<number, StrikeDeltaObject[]>;
  legIVs: Map<number, number>;
}

const PnLChart = ({
  showKeyLevels,
  showLegend,
  chartHeight = 500,
  forceDefaultZoom,
  setForceDefaultZoom,
  isFullscreen,
  onLegChange,
  onLegDelete,
  strikeDataPerExpiration,
}: PnLChartProps) => {
  const theme = useTheme();

  // Only subscribe to the data we actually need
  const {
    sym,
    isLoading,
    ivSettings,
    legs,
    currentKeyLevels,
    allGreeksData,
    shares,
    currentPrice,
    baseDate,
  } = useOptionsCalculatorData();
  const {
    profitLoss,
    profitLossAtExpiration,
    unadjustedIvPnl,
    isCalculating,
    calculationError,
    hasValidInputs,
  } = useOptionsCalculatorComputed();

  const latestLegExpiration = useMemo(() => {
    if (legs.length === 0) {
      return 0;
    }
    return Math.min(...legs.map((leg) => leg.expiry));
  }, [legs]);

  const isEmpty = useMemo(() => {
    return (
      !hasValidInputs &&
      !isCalculating &&
      !isLoading &&
      !profitLoss &&
      !profitLossAtExpiration
    );
  }, [
    hasValidInputs,
    isCalculating,
    isLoading,
    profitLoss,
    profitLossAtExpiration,
  ]);

  // Calculate expirations from greeks data
  const expirationOptions = useMemo(() => {
    const greeksExpirations = allGreeksData?.get(sym);
    if (!greeksExpirations) {
      return [];
    }
    return Object.keys(greeksExpirations).map((key) => Number(key));
  }, [allGreeksData, sym]);

  const disabledExpirationOptions = useMemo(() => {
    const greeks = allGreeksData?.get(sym);
    if (!greeks) {
      return [];
    }

    return expirationOptions.filter((utcTime) => {
      const rawGreeksObject = greeks[utcTime];
      const relatedStrikes =
        rawGreeksObject != null ? Object.keys(rawGreeksObject).map(Number) : [];

      // Check if all leg strikes are available in this expiration's data
      const legStrikes = legs.map((leg) => leg.strike);
      const hasAllLegStrikes = legStrikes.every((strike) =>
        relatedStrikes.includes(strike),
      );
      return !hasAllLegStrikes;
    });
  }, [legs, allGreeksData, sym, expirationOptions]);

  if (isEmpty) {
    return (
      <Stack
        sx={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          py: 3,
        }}
      >
        {/* Subtle background pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `radial-gradient(circle at 25% 25%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 50%), radial-gradient(circle at 75% 75%, ${alpha(theme.palette.secondary.main, 0.05)} 0%, transparent 50%)`,
            zIndex: 0,
          }}
        />

        <Fade in timeout={800}>
          <Stack
            spacing={3}
            alignItems="center"
            sx={{
              textAlign: 'center',
              maxWidth: 400,
              px: 3,
              zIndex: 1,
            }}
          >
            {/* Icon with subtle animation */}
            <Box
              sx={{
                p: 3,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                  '50%': {
                    transform: 'scale(1.05)',
                    opacity: 0.8,
                  },
                },
              }}
            >
              <TrendingUpIcon
                sx={{
                  fontSize: 48,
                  color: theme.palette.primary.main,
                  opacity: 0.7,
                }}
              />
            </Box>

            {/* Main heading */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              Ready to Analyze
            </Typography>

            {/* Descriptive text */}
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              Add option legs or shares to your position to see the profit &
              loss chart visualization
            </Typography>

            {/* Helpful tips */}
            <Stack
              spacing={1}
              sx={{
                width: '100%',
                p: 2,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.info.main, 0.05),
                border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.info.main,
                  fontWeight: 500,
                  mb: 0.5,
                }}
              >
                💡 Quick Start Tips:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.875rem',
                }}
              >
                • Use the position builder to add option legs
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.875rem',
                }}
              >
                • Add underlying shares for covered strategies
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.875rem',
                }}
              >
                • Adjust strikes and quantities to see real-time PnL
              </Typography>
            </Stack>
          </Stack>
        </Fade>
      </Stack>
    );
  }

  // Show loading skeleton only for initial data loading (greeks, prices)
  if (isLoading) {
    return (
      <Stack
        sx={{
          width: '100%',
          height: '100%',
          gap: '24px',
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ChartWatermarkContainer size={15}>
            <LoadingSkeleton />
          </ChartWatermarkContainer>
        </Box>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        height: isFullscreen ? '92%' : chartHeight,
        // flex: 1 defaults to flex-basis: 0%, which overrides `height` for actual
        // sizing - minHeight is what makes the chart actually reach chartHeight.
        minHeight: isFullscreen ? undefined : chartHeight,
        display: 'grid',
        gridTemplateRows: '1fr',
      }}
    >
      <ChartWatermarkContainer size={15}>
        <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
          {profitLoss && profitLossAtExpiration && unadjustedIvPnl ? (
            <Chart
              data={profitLoss}
              dataAtExpiration={
                latestLegExpiration !== baseDate
                  ? profitLossAtExpiration
                  : undefined
              }
              dataUnadjustedIv={
                hasModifiedIVSettings(
                  ivSettings,
                  legs.map((leg) => leg.expiry),
                )
                  ? unadjustedIvPnl
                  : undefined
              }
              currentPrice={currentPrice}
              currentKeyLevels={currentKeyLevels}
              showKeyLevels={showKeyLevels}
              showLegend={showLegend}
              forceDefaultZoom={forceDefaultZoom}
              setForceDefaultZoom={setForceDefaultZoom}
              legs={legs}
              shares={shares}
              sgGreeks={allGreeksData?.get(sym)}
              onLegChange={onLegChange}
              strikeDataPerExpiration={strikeDataPerExpiration}
              sym={sym}
              timeToExpiration={baseDate}
              disabledExpirationOptions={disabledExpirationOptions}
              expirationOptions={expirationOptions}
              onLegDelete={onLegDelete}
            />
          ) : (
            // Show a placeholder while first calculation is running
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <Typography variant="body2">Preparing chart...</Typography>
            </Box>
          )}
          <LoadingOverlay
            isCalculating={isCalculating}
            calculationError={calculationError}
          />
        </Box>
      </ChartWatermarkContainer>
    </Box>
  );
};

export default PnLChart;
